import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";

import { Page } from "@/components/Page";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { H1, P } from "@/components/ui/typography";
import { createStickySet, listTagsQueryOptions } from "@/lib/api";

export const Route = createFileRoute(
  "/app/_sidebar-layout/sticky-sets/create",
)({
  component: CreateStickySetPage,
  head: () => ({ meta: [{ title: "Create Sticky Set" }] }),
});

function CreateStickySetPage() {
  const navigate = useNavigate({ from: "/app/sticky-sets/create" });
  const [scopeMode, setScopeMode] = useState<"explicit" | "tag">("explicit");
  const [tagId, setTagId] = useState<string>("");
  const { data: tags } = useQuery(listTagsQueryOptions());

  const create = useMutation({
    mutationFn: async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      const form = new FormData(e.target as HTMLFormElement);
      const set = await createStickySet({
        display_name: form.get("display_name") as string,
        description: (form.get("description") as string) || undefined,
        scope_mode: scopeMode,
        scope_tag_id:
          scopeMode === "tag" && tagId ? parseInt(tagId) : undefined,
      });
      await navigate({
        to: "/app/sticky-sets/$setId/view",
        params: { setId: `${set.id}` },
      });
    },
  });

  return (
    <Page className="md:gap-10">
      <H1>Create Sticky Set</H1>
      <form
        className="flex flex-col gap-8 max-w-xl"
        onSubmit={(e) => create.mutate(e)}
      >
        <div className="flex flex-col gap-2">
          <Label htmlFor="display_name">Name</Label>
          <Input
            required
            id="display_name"
            name="display_name"
            type="text"
            placeholder="Election alerts"
          />
        </div>

        <div className="flex flex-col gap-2">
          <Label htmlFor="description">Description (optional)</Label>
          <Input
            id="description"
            name="description"
            type="text"
            placeholder="Standing notices that run on every news feed"
          />
        </div>

        <div className="flex flex-col gap-2">
          <Label htmlFor="scope_mode">Which feeds</Label>
          <Select
            value={scopeMode}
            onValueChange={(v) => setScopeMode(v as "explicit" | "tag")}
          >
            <SelectTrigger id="scope_mode">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="explicit">Feeds I pick</SelectItem>
              <SelectItem value="tag">Every feed with a tag</SelectItem>
            </SelectContent>
          </Select>
          <P className="text-sm text-slate-dim">
            {scopeMode === "tag"
              ? "New feeds join this set as soon as you give them the tag."
              : "You'll choose the feeds on the next screen, or in bulk from your feeds list."}
          </P>
        </div>

        {scopeMode === "tag" && (
          <div className="flex flex-col gap-2">
            <Label htmlFor="scope_tag">Tag</Label>
            <Select value={tagId} onValueChange={setTagId}>
              <SelectTrigger id="scope_tag">
                <SelectValue placeholder="Pick a tag" />
              </SelectTrigger>
              <SelectContent>
                {(tags ?? []).map((tag) => (
                  <SelectItem key={tag.id} value={`${tag.id}`}>
                    {tag.display_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {tags && tags.length === 0 && (
              <P className="text-sm text-slate-dim">
                You don&apos;t have any tags yet. Tag some feeds on your feeds
                list first, or choose &ldquo;Feeds I pick&rdquo;.
              </P>
            )}
          </div>
        )}

        <div className="flex">
          <div className="flex-1" />
          <Button
            type="submit"
            isLoading={create.isPending}
            disabled={scopeMode === "tag" && !tagId}
          >
            Create
          </Button>
        </div>
        {create.error && (
          <div className="text-red-normal text-sm">{create.error.message}</div>
        )}
      </form>
    </Page>
  );
}
